import React, { useState, useEffect, useMemo, lazy, Suspense } from "react";
import "./Home.css";
import { conteoParams } from "../../interpreter/role";
import { monthTechnician } from "../../interpreter/technician";
import { Headers } from "../../layouts/headers/headers";
import { MainContent } from "../../layouts/MainContent";
import { HeadersCentral } from "../../layouts/headers-central/headers-central";
import { useAuth } from "../../contexts/authContexts";
import { monthTranslations } from "../../services/diccionaryMonth";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';

import { MdArrowForward, MdGroup, MdConstruction, MdInbox, MdAssignmentTurnedIn } from "react-icons/md";

const UserModal = lazy(() => import("../../components/modals/profileUser"));
const DetailTechnician = lazy(() => import("../../components/modals/detailsContent").then(m => ({ default: m.DetailTechnician })));
const DetailClient = lazy(() => import("../../components/modals/detailsContent").then(m => ({ default: m.DetailClient })));
const DetailRequest = lazy(() => import("../../components/modals/detailsContent").then(m => ({ default: m.DetailRequest })));
const DetailService = lazy(() => import("../../components/modals/detailsContent").then(m => ({ default: m.DetailService })));

ChartJS.register(ArcElement, Tooltip, Legend);

const CHART_COLORS = ["#2980B9", "#34495E", "#5DADE2", "#2C3E50", "#7FB3D3", "#566573"];

const Home = () => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [conteo, setConteo] = useState({ clientesInternos: 0, tecnicos: 0, solicitudes: 0, servicios: 0 });
  const [technicianStats, setTechnicianStats] = useState({ months: [], counts: [] });

  const { user } = useAuth();
  const tieneRol = user?.role?.some(role => role.name === "Administrativo");
  const tienePermiso = user?.permissions?.some(permiso => permiso.name === "ver_dashboard");

  useEffect(() => {
    if (!tieneRol && !tienePermiso) {
      alert("⚠️ Acceso denegado: El usuario no tiene los permisos adecuados.");
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await conteoParams();
        if (response.success) {
          setConteo({
            clientesInternos: response.cont.client || 0,
            tecnicos: response.cont.technician || 0,
            solicitudes: response.cont.requests || 0,
            servicios: response.cont.service || 0,
          });
        }

        const responseDatos = await monthTechnician();
        if (responseDatos.status === 1) {
          const months = responseDatos.content.map(item => monthTranslations[item.month] || item.month);
          const counts = responseDatos.content.map(item => item.technicians_count);
          setTechnicianStats({ months, counts });
        }
      } catch (error) {
        console.error("❌ Error al obtener los datos:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const openModal = (type) => setModalType(type);
  const closeModal = () => setModalType(null);

  const data = useMemo(() => ({
    labels: technicianStats.months,
    datasets: [
      {
        label: "Técnicos a vencer",
        data: technicianStats.counts,
        backgroundColor: technicianStats.counts.map((_, i) => CHART_COLORS[i % CHART_COLORS.length]),
        borderColor: "#FFFFFF",
        borderWidth: 2
      },
    ],
  }), [technicianStats]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          boxWidth: 14,
          padding: 12,
          font: { size: 14 },
          color: '#34495E',
        },
      },
    },
    cutout: '60%',
  }), []);

  const dashboardItems = [
    { label: 'Clientes Internos', count: conteo.clientesInternos, icon: <MdGroup size={32} />, variant: 'clients', modal: 'client' },
    { label: 'Técnicos', count: conteo.tecnicos, icon: <MdConstruction size={32} />, variant: 'technicians', modal: 'technician' },
    { label: 'Solicitudes', count: conteo.solicitudes, icon: <MdInbox size={32} />, variant: 'requests', modal: 'request' },
    { label: 'Servicios', count: conteo.servicios, icon: <MdAssignmentTurnedIn size={32} />, variant: 'services', modal: 'service' }
  ];

  return (
    <div className="flex h-screen">
      <Headers />
      <MainContent>
        <HeadersCentral showDropdown={showDropdown} setShowDropdown={setShowDropdown} openModal={() => openModal('user')} />

        <main className="home-page p-6">
          <div className="home-page__header">
            <h1 className="home-page__title">Dashboard</h1>
            <p className="home-page__subtitle">
              ¡Bienvenido al dashboard de <strong>Abogados</strong>!
            </p>
          </div>

          {loading ? (
            <div className="home-loading">
              <div className="home-loading__spinner" role="status" aria-label="Cargando dashboard" />
            </div>
          ) : (
            <>
              <div className="home-stats">
                {dashboardItems.map((item, idx) => (
                  <div key={idx} className={`home-stat-card home-stat-card--${item.variant}`}>
                    <div className="home-stat-card__top">
                      <div>
                        <div className="home-stat-card__icon">{item.icon}</div>
                        <h2 className="home-stat-card__label">{item.label}</h2>
                        <p className="home-stat-card__count">{item.count}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => openModal(item.modal)}
                        className="home-stat-card__action"
                        aria-label={`Ver detalle de ${item.label}`}
                      >
                        <MdArrowForward size={20} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="home-chart-card">
                <h2 className="home-chart-card__title">Estadísticas de Técnicos por Mes</h2>
                <div className="home-chart-card__body">
                  <div className="home-chart-card__canvas">
                    <Doughnut
                      key={technicianStats.months.join("-") || "chart-empty"}
                      data={data}
                      options={chartOptions}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </main>
      </MainContent>
      <Suspense fallback={null}>
        {modalType === 'user' && <UserModal isOpen onClose={closeModal} user={user} />}
        {modalType === 'technician' && <DetailTechnician isOpen onClose={closeModal} />}
        {modalType === 'client' && <DetailClient isOpen onClose={closeModal} />}
        {modalType === 'request' && <DetailRequest isOpen onClose={closeModal} />}
        {modalType === 'service' && <DetailService isOpen onClose={closeModal} technicianData={0} />}
      </Suspense>
    </div>
  );
};

export default Home;
